import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CONTRACT_STATUS_OPTIONS from '../config/contractStatusOptions';

export default function OutsourceList() {
  const [outsources, setOutsources] = useState([]);
  const [contracts, setContracts] = useState({});

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [targetOutsource, setTargetOutsource] = useState(null);

  const [form, setForm] = useState({
    name: '',
    postal_code: '',
    address: '',
    tel: '',
    email: '',
    contact_name: '',
    contact_email: '',
    contact_tel: '',
    memo: '',
  });

  const [contractForm, setContractForm] = useState({
    title: '',
    status: '有効',
    startDate: '',
    endDate: '',
  });

  /* =========================
   * load
   * ========================= */
  const loadOutsources = async () => {
    const res = await api.get('/api/outsources');
    const rows = (res.data || []).map(o => ({
      ...o,
      id: o.id.toString(),
    }));
    setOutsources(rows);

    const c = {};
    await Promise.all(
      rows.map(async (o) => {
        try {
          const r = await api.get(`/api/outsources/${o.id}/contracts`);
          c[o.id] = r.data || [];
        } catch {
          c[o.id] = [];
        }
      })
    );
    setContracts(c);
  };

  useEffect(() => {
    loadOutsources();
  }, []);

  /* =========================
   * handlers
   * ========================= */
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm({
      name: '',
      postal_code: '',
      address: '',
      tel: '',
      email: '',
      contact_name: '',
      contact_email: '',
      contact_tel: '',
      memo: '',
    });
  };

  const fetchAddressByPostalCode = async (postalCode) => {
    const zip = postalCode.replace(/[^0-9]/g, '');
    if (zip.length !== 7) return;

    try {
      const res = await fetch(
        `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`
      );
      const data = await res.json();
      if (data.results?.length) {
        const r = data.results[0];
        setForm(f => ({
          ...f,
          address: `${r.address1}${r.address2}${r.address3}`,
        }));
      }
    } catch {
      // noop
    }
  };

  const handleSubmit = async () => {
    const payload = { ...form };

    if (editId) {
      await api.put(`/api/outsources/${editId}`, payload);
    } else {
      await api.post('/api/outsources', payload);
    }

    setOpen(false);
    setEditId(null);
    resetForm();
    loadOutsources();
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      name: row.name || '',
      postal_code: row.postal_code || '',
      address: row.address || '',
      tel: row.tel || '',
      email: row.email || '',
      contact_name: row.contact_name || '',
      contact_email: row.contact_email || '',
      contact_tel: row.contact_tel || '',
      memo: row.memo || '',
    });
    setOpen(true); // ★必ず最後
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    try {
      await api.delete(`/api/outsources/${id}`);
      loadOutsources();
    } catch (e) {
      alert(`削除できませんでした: ${e.response?.data?.error || e.message}`);
    }
  };

  /* =========================
   * contract
   * ========================= */
  const handleContractSubmit = async () => {
    await api.post('/api/contracts', {
      outsourceId: targetOutsource.id,
      title: contractForm.title,
      status: contractForm.status,
      startDate: contractForm.startDate || null,
      endDate: contractForm.endDate || null,
    });

    setContractDialogOpen(false);
    setContractForm({ title: '', status: '有効', startDate: '', endDate: '' });
    setTargetOutsource(null);
    loadOutsources();
  };

  /* =========================
   * columns
   * ========================= */
  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },

    {
      field: 'name',
      headerName: '外注先名（会社名）',
      width: 240,
      renderCell: (p) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography fontWeight="bold">
            {p.row.name || '-'}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'contact',
      headerName: '担当者',
      width: 260,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.4 }}>
          <Typography fontWeight="bold">
            {p.row.contact_name || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.contact_email || ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {p.row.contact_tel || ''}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'addressBlock',
      headerName: '住所',
      width: 320,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.4 }}>
          <Typography variant="caption" color="text.secondary">
            {p.row.postal_code ? `〒${p.row.postal_code}` : ''}
          </Typography>
          <Typography variant="body2">
            {p.row.address || ''}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'contract',
      headerName: '契約',
      width: 220,
      renderCell: (p) => {
        const list = contracts[p.row.id] || [];
        if (!list.length) {
          return (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setTargetOutsource(p.row);
                setContractDialogOpen(true);
              }}
            >
              未契約
            </Button>
          );
        }
        const latest = list[0];
        return (
          <Box>
            <Typography variant="body2">
              {latest.title || ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {latest.status || ''}
              {latest.endDate ? ` / 〜${String(latest.endDate).slice(0, 10)}` : ''}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (p) => (
        <>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(p.row);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(p.row.id);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  /* =========================
   * render
   * ========================= */
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => {
            resetForm();
            setEditId(null);
            setOpen(true);
          }}
        >
          新規外注先
        </Button>
      </Box>

      <DataGrid
        rows={outsources}
        columns={columns}
        autoHeight
        pageSizeOptions={[10, 50]}
        disableRowSelectionOnClick
        getRowHeight={() => 72}
      />

      {/* 外注先 登録 / 編集 */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          外注先を{editId ? '編集' : '登録'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="外注先名（会社名）"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
              <TextField
                label="郵便番号"
                name="postal_code"
                value={form.postal_code}
                onChange={handleChange}
                onBlur={(e) => fetchAddressByPostalCode(e.target.value)}
                inputProps={{ inputMode: 'numeric' }}
              />
              <TextField
                label="住所"
                name="address"
                value={form.address}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="電話番号"
                name="tel"
                value={form.tel}
                onChange={handleChange}
              />
              <TextField
                label="メール"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField
                label="担当者名"
                name="contact_name"
                value={form.contact_name}
                onChange={handleChange}
              />
              <TextField
                label="担当者メール"
                name="contact_email"
                value={form.contact_email}
                onChange={handleChange}
              />
              <TextField
                label="担当者電話"
                name="contact_tel"
                value={form.contact_tel}
                onChange={handleChange}
              />
            </Box>

            <TextField
              label="備考"
              name="memo"
              value={form.memo}
              onChange={handleChange}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editId ? '更新' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 契約登録 */}
      <Dialog
        open={contractDialogOpen}
        onClose={() => setContractDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          契約登録（{targetOutsource?.name || ''}）
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="契約タイトル"
            margin="dense"
            value={contractForm.title}
            onChange={(e) =>
              setContractForm(f => ({ ...f, title: e.target.value }))
            }
          />

          <Autocomplete
            options={CONTRACT_STATUS_OPTIONS}
            getOptionLabel={(o) => o.label}
            value={
              CONTRACT_STATUS_OPTIONS.find(o => o.value === contractForm.status) || null
            }
            onChange={(_, v) =>
              setContractForm(f => ({ ...f, status: v ? v.value : '' }))
            }
            renderInput={(p) => (
              <TextField {...p} label="ステータス" margin="dense" fullWidth />
            )}
            isOptionEqualToValue={(o, v) => o.value === v.value}
          />

          <TextField
            fullWidth
            type="date"
            label="開始日"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={contractForm.startDate}
            onChange={(e) =>
              setContractForm(f => ({ ...f, startDate: e.target.value }))
            }
          />

          <TextField
            fullWidth
            type="date"
            label="終了日"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={contractForm.endDate}
            onChange={(e) =>
              setContractForm(f => ({ ...f, endDate: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleContractSubmit}>
            登録
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
